import { CampoEnum } from "@/enums/campos";
import { CentroCustoFormSchema } from "@/interfaces/centro-custo.interface";
import { CredencialFormSchema } from "@/interfaces/credencial.interface";
import { EquipeFormSchema } from "@/interfaces/equipe.interface";
import { IEvento } from "@/interfaces/evento.interface";
import { FormaPagamentoFormSchema } from "@/interfaces/formas-pagamento.interface";
import { IgrejaFormSchema } from "@/interfaces/igreja.interface";
import { MarcadorFormSchema } from "@/interfaces/marcador.interface";
import { IPadrinho } from "@/interfaces/padrinho.interface";
import { IQuarto } from "@/interfaces/quarto.interface";
import { IReuniao } from "@/interfaces/reuniao.interface";
import { GrupoDto, ProdutoDto } from "@/interfaces/produto.interface";
import {
  configuracaocamposModel,
  configuracaocamposequipesModel,
  mensagemsModel,
} from "@/interfaces/zod";
import { invalidateContextoEvento } from "@/lib/invalidate-contexto-evento";
import {
  findEventoById,
  findEventoInRtkQueries,
  hasValidEventoId,
  mergeSelectedEventoNaLista,
} from "@/lib/evento-selecionado";
import { setEventData } from "@/lib/gtm";
import { eventosAPI } from "@/services/eventos.service";
import {
  ActionCreatorWithPayload,
  PayloadAction,
  createListenerMiddleware,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { z } from "zod";
import type { RootState } from "../store";
import { resetState, clearAuthStateOnly } from "./auth.slice";

/** Evita ciclos eventos ↔ configuracoes / eventos → general. */
const GENERAL_SET_LOGIN_ID = "generalSlice/setLoginId";
const CONFIGURACOES_SET_CONFIGURACOES = "configuracoes/SetConfiguracoes";

export interface EventoState {
  Eventos: Array<IEvento>;
  EventoId: number | null;
  Campos: Array<z.infer<typeof configuracaocamposModel>>;
  CamposEquipe: Array<z.infer<typeof configuracaocamposequipesModel>>;
  Padrinhos: Array<IPadrinho>;
  Reunioes: Array<IReuniao>;
  QuartosVoluntarios: Array<IQuarto>;
  QuartosParticipantes: Array<IQuarto>;
  Mensagens: Array<z.infer<typeof mensagemsModel>>;
  FormasPagamento: Array<z.infer<typeof FormaPagamentoFormSchema>>;
  CentroCustos: Array<z.infer<typeof CentroCustoFormSchema>>;
  Igrejas: Array<z.infer<typeof IgrejaFormSchema>>;
  Etiquetas: Array<z.infer<typeof MarcadorFormSchema>>;
  EquipesFormulario: Array<z.infer<typeof EquipeFormSchema>>;
  Equipes: Array<z.infer<typeof EquipeFormSchema>>;
  Credenciais: Array<z.infer<typeof CredencialFormSchema>>;
  Produtos: Array<ProdutoDto>;
  Grupos: Array<GrupoDto>;
}

/** Zera metadados espelhados (colunas, filtros, etc.) — anti-flash ao trocar evento. */
function clearEventoMetadados(state: EventoState) {
  state.Campos = [];
  state.CamposEquipe = [];
  state.EquipesFormulario = [];
  state.Equipes = [];
  state.QuartosParticipantes = [];
  state.QuartosVoluntarios = [];
  state.Padrinhos = [];
  state.Etiquetas = [];
  state.Reunioes = [];
  state.Mensagens = [];
  state.FormasPagamento = [];
  state.CentroCustos = [];
  state.Igrejas = [];
  state.Credenciais = [];
  state.Produtos = [];
  state.Grupos = [];
}

const initialState: EventoState = {
  Eventos: [],
  EventoId:
    Number(new URL(location.href).searchParams.get("eventoId")) ||
    (localStorage.getItem("EventoId") as unknown as number),
  Campos: [],
  CamposEquipe: [],
  EquipesFormulario: [],
  Equipes: [],
  QuartosParticipantes: [],
  QuartosVoluntarios: [],
  Reunioes: [],
  Padrinhos: [],
  Etiquetas: [],
  FormasPagamento: [],
  CentroCustos: [],
  Igrejas: [],
  Mensagens: [],
  Credenciais: [],
  Produtos: [],
  Grupos: [],
};

export const eventosSlice = createSlice({
  name: "eventos",
  initialState,
  reducers: {
    SetEventoId(state: EventoState, { payload }: PayloadAction<number | null>) {
      const nextId =
        payload == null || Number(payload) <= 0 || Number.isNaN(Number(payload))
          ? null
          : Number(payload);
      const previousId = state.EventoId;
      localStorage.setItem("EventoId", nextId?.toString() || "");
      state.EventoId = nextId;

      // Evita colunas/metadados do evento anterior até a onda de fetch completar.
      if (Number(previousId) !== Number(nextId)) {
        clearEventoMetadados(state);
      }

      // Enviar dados do evento selecionado para o GTM dataLayer
      const eventoSelecionado = findEventoById(state.Eventos, nextId);
      const eventoTitulo = eventoSelecionado?.Titulo;
      setEventData(nextId, eventoTitulo);
    },

    SetEventos(state: EventoState, { payload }: PayloadAction<Array<IEvento>>) {
      const selectedAtual = findEventoById(state.Eventos, state.EventoId);
      // Refetch/filtro pode devolver pagina sem o evento atual — manter visivel.
      state.Eventos = mergeSelectedEventoNaLista(
        payload,
        selectedAtual,
        state.EventoId,
      );
      // Só auto-seleciona na carga inicial (sem EventoId). Se o id atual sumiu da
      // lista (ex.: filtro de busca no select), manter — evita troca silenciosa
      // com formulários abertos salvando no evento errado.
      if (!hasValidEventoId(state.EventoId) && state.Eventos?.length > 0) {
        state.EventoId = state.Eventos[0]?.Id ?? null;
      }
      localStorage.setItem("EventoId", state.EventoId?.toString() || "");

      // Enviar dados do evento selecionado para o GTM dataLayer
      if (state.EventoId) {
        const eventoSelecionado = state.Eventos?.find(
          (ev) => ev.Id === state.EventoId
        );
        const eventoTitulo = eventoSelecionado?.Titulo;
        setEventData(state.EventoId, eventoTitulo);
      }
    },

    SetCampos(state: EventoState, { payload }: PayloadAction<any>) {
      // Campos/CamposEquipe já trazem personalizados aninhados em `campospersonalizados`.
      // Não concatenar CamposPersonalizados* — isso duplicava cada campo no formulário.
      state.Campos = payload.Campos ?? [];
      state.CamposEquipe = payload.CamposEquipe ?? [];
    },
    SetEquipes(state: EventoState, { payload }: PayloadAction<EventoState>) {
      state.Equipes = payload.Equipes;
    },
    SetEquipesFormulario(
      state: EventoState,
      { payload }: PayloadAction<EventoState>
    ) {
      state.EquipesFormulario = payload.EquipesFormulario;
    },
    SetPadrinhos(state: EventoState, { payload }: PayloadAction<EventoState>) {
      state.Padrinhos = payload.Padrinhos;
    },
    SetEtiquetas(state: EventoState, { payload }: PayloadAction<EventoState>) {
      state.Etiquetas = payload.Etiquetas;
    },
    SetIgrejas(
      state: EventoState,
      { payload }: PayloadAction<Pick<EventoState, "Igrejas">>,
    ) {
      state.Igrejas = payload.Igrejas;
    },
    SetMensagens(state: EventoState, { payload }: PayloadAction<EventoState>) {
      state.Mensagens = payload.Mensagens;
    },
    SetFormasPagamentos(
      state: EventoState,
      { payload }: PayloadAction<EventoState>
    ) {
      state.FormasPagamento = payload.FormasPagamento;
    },
    SetCentroCustos(
      state: EventoState,
      { payload }: PayloadAction<EventoState>
    ) {
      state.CentroCustos = payload.CentroCustos;
    },

    SetReunioes(state: EventoState, { payload }: PayloadAction<EventoState>) {
      state.Reunioes = payload.Reunioes;
    },
    SetCredenciais(
      state: EventoState,
      { payload }: PayloadAction<EventoState>
    ) {
      state.Credenciais = payload.Credenciais;
    },
    SetQuartosParticipantes(
      state: EventoState,
      { payload }: PayloadAction<{ Quartos: IQuarto[] }>
    ) {
      state.QuartosParticipantes = payload.Quartos;
    },
    SetQuartosVoluntarios(
      state: EventoState,
      { payload }: PayloadAction<{ Quartos: IQuarto[] }>
    ) {
      state.QuartosVoluntarios = payload.Quartos;
    },
    SetProdutos(
      state: EventoState,
      { payload }: PayloadAction<Array<ProdutoDto>>
    ) {
      state.Produtos = payload;
    },
    SetGrupos(
      state: EventoState,
      { payload }: PayloadAction<{ Grupos: Array<GrupoDto> }>
    ) {
      state.Grupos = payload.Grupos;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetState, (state) => {
        state.Eventos = [];
        state.EventoId = null;
        state.Campos = [];
        state.CamposEquipe = [];
        state.EquipesFormulario = [];
        state.Equipes = [];
        state.QuartosParticipantes = [];
        state.QuartosVoluntarios = [];
        state.Padrinhos = [];
        state.Etiquetas = [];
        state.Reunioes = [];
        state.Mensagens = [];
        state.FormasPagamento = [];
        state.CentroCustos = [];
        state.Igrejas = [];
        state.Mensagens = [];
        state.Credenciais = [];
        state.Produtos = [];

        localStorage.setItem("EventoId", "");
      })
      .addCase(clearAuthStateOnly, (state) => {
        state.Eventos = [];
        state.EventoId = null;
        state.Campos = [];
        state.CamposEquipe = [];
        state.EquipesFormulario = [];
        state.Equipes = [];
        state.QuartosParticipantes = [];
        state.QuartosVoluntarios = [];
        state.Padrinhos = [];
        state.Etiquetas = [];
        state.Reunioes = [];
        state.Mensagens = [];
        state.FormasPagamento = [];
        state.CentroCustos = [];
        state.Igrejas = [];
        state.Mensagens = [];
        state.Credenciais = [];
        state.Produtos = [];

        localStorage.setItem("EventoId", "");
      })
      .addMatcher(
        (action) => action.type === CONFIGURACOES_SET_CONFIGURACOES,
        (state) => {
          // Antes: Eventos = null. Isso apagava o selecionado no meio de um
          // refetch e esvaziava Credenciais/select ate um F5.
          if (!Array.isArray(state.Eventos)) {
            state.Eventos = [];
          }
        },
      )
      .addMatcher(
        (action) => action.type === GENERAL_SET_LOGIN_ID,
        (state) => {
          state.EventoId = null;
          state.Eventos = [];
          state.Campos = [];
          state.CamposEquipe = [];
          state.EquipesFormulario = [];
          state.Equipes = [];
          state.QuartosParticipantes = [];
          state.QuartosVoluntarios = [];
          state.Padrinhos = [];
          state.Etiquetas = [];
          state.Reunioes = [];
          state.Mensagens = [];
          state.FormasPagamento = [];
          state.CentroCustos = [];
          state.Igrejas = [];
          state.Mensagens = [];
          state.Credenciais = [];
          state.Produtos = [];
        },
      );
  },
});

export const eventosListenerMiddleware = createListenerMiddleware();
eventosListenerMiddleware.startListening({
  predicate: (action, currentState, previousState) => {
    if (action.type == eventosSlice.actions.SetEventos.type) {
      if ((previousState as RootState).eventosReducer.Eventos?.length == 0) {
        return true;
      }
    }

    if (action.type == eventosSlice.actions.SetEventoId.type) {
      return true;
    }

    return false;
  },
  effect: async (action, listenerApi) => {
    const { EventoId } = (listenerApi.getState() as RootState).eventosReducer;
    listenerApi.cancelActiveListeners();

    const previousEventoId =
      action.type === eventosSlice.actions.SetEventoId.type
        ? (listenerApi.getOriginalState() as RootState).eventosReducer.EventoId
        : EventoId;
    const eventoIdMudou =
      action.type === eventosSlice.actions.SetEventoId.type &&
      Number(previousEventoId) !== Number(EventoId);

    // Failsafe: só quando o EventoId de fato mudou (evita refetch em re-dispatch do mesmo id).
    if (eventoIdMudou) {
      invalidateContextoEvento(listenerApi.dispatch);
    }

    // Re-dispatch do mesmo EventoId (ex.: abrir sheet da home) — não reaquece metadados
    // já carregados. Se Igrejas/Campos ainda estão vazios (reload da inscrição pública
    // com o mesmo EventoId no localStorage), precisa buscar de novo.
    const metadadosAtuais = (listenerApi.getState() as RootState).eventosReducer;
    const metadadosCriticosVazios =
      (metadadosAtuais.Igrejas?.length ?? 0) === 0 ||
      (metadadosAtuais.Campos?.length ?? 0) === 0;
    if (
      action.type === eventosSlice.actions.SetEventoId.type &&
      !eventoIdMudou &&
      !metadadosCriticosVazios
    ) {
      return;
    }

    const isBackofficeEventos =
      window.location.pathname.includes("backoffice");
    if (EventoId && !isBackofficeEventos) {
      const eventoId = EventoId as number;
      const isSistema = window.location.pathname.includes("sistema");

      // Dispara a query e sincroniza o resultado (de fetch OU de cache) para o slice.
      // Em cache-hit o `onQueryStarted` não roda; sem este sync o slice ficaria com os
      // dados do evento anterior (stale) ao voltar para um evento já cacheado — o que
      // se torna comum agora que o cache fica quente (keepUnusedDataFor: 300).
      // Guard: só espelha se o EventoId atual ainda for o da query (prefetch/race).
      const initiateAndSync = (
        endpoint: { initiate: (arg: number) => any },
        sliceAction?: ActionCreatorWithPayload<any>,
      ) => {
        const promise = listenerApi.dispatch(endpoint.initiate(eventoId));
        if (sliceAction) {
          promise
            .unwrap()
            .then((data: any) => {
              const current = (listenerApi.getState() as RootState).eventosReducer
                .EventoId;
              if (Number(current) !== Number(eventoId)) return;
              listenerApi.dispatch(sliceAction(data));
            })
            .catch(() => { });
        }
        return promise;
      };

      // Onda 1 (crítica): metadados que definem colunas/células da listagem.
      // Sem forceRefetch — o RTK deduplica e troca de cache quando o EventoId muda.
      // Disparada imediatamente para não competir com a request principal /participantes.
      initiateAndSync(eventosAPI.endpoints.getCampos, eventosSlice.actions.SetCampos);
      if (isSistema) {
        initiateAndSync(eventosAPI.endpoints.getEtiquetas, eventosSlice.actions.SetEtiquetas);
        // getCirculos não tem espelho no slice (consumido direto via RTK Query) — só aquece o cache.
        initiateAndSync(eventosAPI.endpoints.getCirculos);
      }

      // Onda 2 (não crítica): filtros, modais e outras abas. Adiada para depois
      // do primeiro paint da tabela, evitando saturar o backend com a avalanche
      // de requisições simultâneas que inflava o TTFB de /participantes.
      // listenerApi.delay é cancelável: se o evento mudar, esta onda é abortada.
      await listenerApi.delay(250);

      initiateAndSync(eventosAPI.endpoints.getEquipesEventos, eventosSlice.actions.SetEquipes);
      initiateAndSync(eventosAPI.endpoints.getEquipesFormulario, eventosSlice.actions.SetEquipesFormulario);
      initiateAndSync(eventosAPI.endpoints.getIgrejasEvento, eventosSlice.actions.SetIgrejas);
      initiateAndSync(eventosAPI.endpoints.getProdutos, eventosSlice.actions.SetProdutos);

      if (isSistema) {
        initiateAndSync(eventosAPI.endpoints.getCredenciais, eventosSlice.actions.SetCredenciais);
        initiateAndSync(eventosAPI.endpoints.getPadrinhos, eventosSlice.actions.SetPadrinhos);
        initiateAndSync(eventosAPI.endpoints.getReunioes, eventosSlice.actions.SetReunioes);
        initiateAndSync(eventosAPI.endpoints.getQuartosParticipantes, eventosSlice.actions.SetQuartosParticipantes);
        initiateAndSync(eventosAPI.endpoints.getQuartosVoluntarios, eventosSlice.actions.SetQuartosVoluntarios);
        initiateAndSync(eventosAPI.endpoints.getMensagens, eventosSlice.actions.SetMensagens);
        initiateAndSync(eventosAPI.endpoints.getFormasPagamento, eventosSlice.actions.SetFormasPagamentos);
        initiateAndSync(eventosAPI.endpoints.getCentroCustos, eventosSlice.actions.SetCentroCustos);
        initiateAndSync(eventosAPI.endpoints.getGruposEvento, eventosSlice.actions.SetGrupos);
      }
    }
    // Backoffice: só o failsafe de tags (acima) — sem onda de metadados do sistema.
  },
});

export const eventosReducer = eventosSlice.reducer;
export const {
  SetEventos,
  SetEventoId,
  SetCampos,
  SetEquipes,
  SetEquipesFormulario,
  SetQuartosParticipantes,
  SetQuartosVoluntarios,
  SetPadrinhos,
  SetMensagens,
  SetFormasPagamentos,
  SetCentroCustos,
  SetReunioes,
  SetEtiquetas,
  SetIgrejas,
  SetCredenciais,
  SetProdutos,
  SetGrupos,
} = eventosSlice.actions;

const getEventosSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.Eventos
);

const selectEventosReducer = (state: RootState) => state.eventosReducer;
const selectBaseApiQueries = (state: RootState) => state.baseAPI.queries;

const SelectedEventoSelector = createSelector(
  [selectEventosReducer, selectBaseApiQueries],
  (eventosReducer, queries) => {
    const fromState = findEventoById(
      eventosReducer.Eventos,
      eventosReducer.EventoId,
    );
    if (fromState) return fromState;

    // getEventos (select), getEventosPaginated e getEvento — nao so datatable.
    return findEventoInRtkQueries(queries, eventosReducer.EventoId);
  }
);

/** Item de configuração → enum fixo (string) ou objeto do campo personalizado. */
function mapCampoEventoParaSelector(c: any) {
  if (c?.Tipo) return c;
  if (c?.campospersonalizados?.Tipo) return c.campospersonalizados;
  return CampoEnum[c?.Campo];
}

const CamposEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => {
    return {
      Campos: state.Campos.map(mapCampoEventoParaSelector),
      CamposEquipe: state.CamposEquipe.map(mapCampoEventoParaSelector),
    };
  }
);

const CamposFormularioEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => {
    return {
      Campos: state.Campos,
      CamposEquipe: state.CamposEquipe,
    };
  }
);

const PadrinhosEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.Padrinhos
);
const QuartosParticipantesEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.QuartosParticipantes
);
const QuartosVoluntariosEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.QuartosVoluntarios
);
const EtiquetasEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.Etiquetas
);
const MensagensEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.Mensagens
);
const FormasPagamentoEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.FormasPagamento
);
const CentroCustosEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.CentroCustos
);

const ReunioesEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.Reunioes
);
const CredenciaisEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.Credenciais
);
const EquipesEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.Equipes
);
const EquipesFormularioEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.EquipesFormulario
);

const ProdutosEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.Produtos
);

const GruposEventoSelector = createSelector(
  [(state: RootState) => state.eventosReducer],
  (state: EventoState) => state.Grupos
);

export const eventosSelectors = {
  SelectedEventoSelector,
  CamposEventoSelector,
  getEventosSelector,
  PadrinhosEventoSelector,
  ReunioesEventoSelector,
  EquipesEventoSelector,
  EquipesFormularioEventoSelector,
  QuartosParticipantesEventoSelector,
  QuartosVoluntariosEventoSelector,
  EtiquetasEventoSelector,
  MensagensEventoSelector,
  CredenciaisEventoSelector,
  CamposFormularioEventoSelector,
  FormasPagamentoEventoSelector,
  CentroCustosEventoSelector,
  ProdutosEventoSelector,
  GruposEventoSelector,
};
